import { baseApi } from 'app/services/baseApi';
import { DocumentResponse, DocumentsResponse, DocumentSummaryResponse, DocumentVersionResponse } from './documentTypes';
import { Document, DocumentVersion } from 'dataTypes/document';

export const documentApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getDocuments: builder.query<DocumentsResponse, string | void>({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: 'document/',
            method: 'GET',
          };
        return {
          url: `document/?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: ['documents'],
    }),
    getDocument: builder.query<DocumentResponse, number>({
      query: id => {
        return {
          url: `document/${id}?include=["DocumentVersion","files"]`,
          method: 'GET',
        };
      },
      providesTags: ['document'],
    }),
    deleteDocument: builder.mutation({
      query: id => ({
        url: `document/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['documents'],
    }),
    getSelfDocuments: builder.query<DocumentsResponse, string | void>({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: 'document/self/sharedwithme',
            method: 'GET',
          };
        return {
          url: `document/self/sharedwithme/?${queryParams}`,
          method: 'GET',
        };
      },
    }),
    getDocumentsSummary: builder.query<DocumentSummaryResponse, string | void>({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: 'document/summary',
            method: 'GET',
          };
        return {
          url: `document/summary/?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: ['documents'],
    }),
    patchDocument: builder.mutation<DocumentResponse, Partial<Document> & Pick<Document, 'id'>>({
      query: ({ id, ...patch }) => ({
        url: `document/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['documents', 'document'],
    }),
    duplicateDocument: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `document/duplicate/${id}`,
          body: data,
          method: 'POST',
        };
      },
      invalidatesTags: ['documents'],
    }),
    postDocument: builder.mutation<DocumentResponse, Partial<Document>>({
      query: ({ ...data }) => {
        return {
          url: `document/`,
          body: data,
          method: 'POST',
        };
      },
      invalidatesTags: ['documents', 'document'],
    }),
    putLinkDocuments: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `document/linkFiles/${id}`,
          body: data,
          method: 'PUT',
        };
      },
      invalidatesTags: ['documents', 'document'],
    }),
    sendDocument: builder.mutation({
      query: ({ documentId }) => {
        return {
          url: `/docusignsigning/self/sendenvelope/${documentId}`,
          method: 'PUT',
        };
      },
      invalidatesTags: ['documents', 'document'],
    }),

    patchDocumentVersion: builder.mutation<
      DocumentVersionResponse,
      Partial<DocumentVersion> & Pick<DocumentVersion, 'id'>
    >({
      query: ({ id, ...patch }) => ({
        url: `documentversion/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['documents', 'document'],
    }),
    duplicateDocumentVersion: builder.mutation({
      query: ({ documentId, ...data }) => {
        return {
          url: `document/makeCopyFromVersion/${documentId}`,
          body: data,
          method: 'POST',
        };
      },
      invalidatesTags: ['documents'],
    }),
    restoreDocumentVersion: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documentversion/self/restoreVersion/${id}`,
          body: data,
          method: 'PUT',
        };
      },
      invalidatesTags: ['documents', 'document'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useDeleteDocumentMutation,
  useDuplicateDocumentMutation,
  useDuplicateDocumentVersionMutation,
  useGetDocumentQuery,
  useGetDocumentsQuery,
  useGetDocumentsSummaryQuery,
  useGetSelfDocumentsQuery,
  usePatchDocumentMutation,
  usePatchDocumentVersionMutation,
  usePostDocumentMutation,
  usePutLinkDocumentsMutation,
  useRestoreDocumentVersionMutation,
  useSendDocumentMutation,
} = documentApi;
